import { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Table, Text } from '@artprov/design-system';
import { columnasAutorizacion } from '../columnnasAutorizacion';
import DroopdownGestion from './DropdownGestion';
import ModalComprobante from './ModalComprobante';
import { MessagesModal } from '../../../../nueva-solicitud/components/MessagesModal';
import { postGestionAutorizacion, getIdicadoresSolicitud } from '../../../../../services/autorizaciones/autorizaciones.service';
import { FeedbackModal } from '../../../../nueva-solicitud/components/FeedbackModal';
import { getEstadoAutorizantes } from '../../../../../services/mis-solicitudes/solicitudes';
import ModalRechazar from './ModalRechazar';
import ResumenSolicitud from './ResumenSolicitud';
import { toArray } from '../../../../../common/utils/functions/toArray';

const feedbackMessages = {
    aceptar: {
        success: {
            title: "¡Aprobaste esta solicitud con éxito!",
            message: "Ahora pasará a tesorería para seguir con la aprobación."
        },
        error: {
            title: "Error al aprobar la solicitud",
            message: "Hubo un problema al intentar autorizar la solicitud. Verificá tu conexión a internet y volvé a intentarlo en un momento."
        }
    },
    rechazar: {
        success: {
            title: "¡Rechazaste esta solicitud con éxito!",
            message: "¿Querés ver el estado de las autorizaciones o ver una solicitud en particular? Podés hacerlo desde la sección Autorizaciones."
        },
        error: {
            title: "Error al rechazar la solicitud",
            message: "Hubo un problema al intentar rechazar la solicitud. Verificá tu conexión a internet y volvé a intentarlo en un momento."
        }
    }
}

export function ListaRendiciones({ adjuntos, isModalOpen, closeModal, adjunto }) {

    const navigate = useNavigate()
    const { id } = useParams()
    const [showDropdown, setShowDropdown] = useState(false)
    const [indicadores, setIndicadores] = useState(null)
    const [estado, setEstado] = useState(null) // Estado de la solicitud
    const [usuarioSolicitante, setUsuarioSolicitante] = useState(null) // Estado de la solicitud
    const [primerAutorizanteNombre, setPrimerAutorizanteNombre] = useState("-")
    const [openGestionModal, setOpenGestionModal] = useState(false)
    const [actionModal, setActionModal] = useState(null) // valores null o string | "aceptar" o "rechazar"
    const [success, setSuccess] = useState(null) // valores tipo null o string | "success" o "error"
    const [selectedRow, setSelectedRow] = useState([]);

    const gestionarSolicitud = async () => {
        setOpenGestionModal(false)
        const response = await postGestionAutorizacion(actionModal, id)
        const status = response ? "success" : "error"
        setSuccess(status)
    }

    const rechazarSolicitud = async ({ comentario }) => {
        setOpenGestionModal(false)
        const response = await postGestionAutorizacion(actionModal, id, comentario)
        setSuccess(response ? "success" : "error")
    }

    const obtenerIndicadores = async () => {
        const { data } = await getIdicadoresSolicitud(id)
        if (data)
            setIndicadores(data)
    }

    const getEstadoSolicitud = async () => {
        const response = await getEstadoAutorizantes(id)
        const traza = toArray(response?.traza)

        const autorizante = traza.find(dato => dato?.isCurrentUser === true)

        if (autorizante?.estado === "En proceso") {
            setShowDropdown(true)
        }

        const primerAutorizante = traza.find(dato =>
            dato.nivel === 1 && !dato.isCurrentUser
        )

        setPrimerAutorizanteNombre(primerAutorizante?.nombre || "-")

        setEstado(autorizante?.estado || null)
        setUsuarioSolicitante(response?.nombreSolicitante || null)
    }

    useEffect(() => {
        obtenerIndicadores()
        getEstadoSolicitud()
    }, [])

    const autorizaciones = useMemo(() => {
        return (adjuntos || []).map(row => ({
            ...row,
            primerAutorizante: primerAutorizanteNombre || "-"
        }))
    }, [adjuntos, primerAutorizanteNombre])


    const mostrarColumnaPrimerAutorizante =
        Boolean(primerAutorizanteNombre) && primerAutorizanteNombre !== "-";

    const columnasAutorizacionDinamicas = useMemo(() => {
        return columnasAutorizacion.map(col => {
            if (col.key !== "primerAutorizante") return col;
            return { ...col, hidden: !mostrarColumnaPrimerAutorizante };
        });
    }, [mostrarColumnaPrimerAutorizante]);


    return (
        <>
            <div
                className={`container mx-auto flex flex-col h-full  min-h-[800px] items-center my-8 px-2 xl:px-8`}>
                <div className="w-full flex flex-col justify-center gap-y-1 p-2">
                    <Breadcrumb
                        customItems={getBreadCrumbs()}
                        onClick={(href) => {
                            if (href) navigate(href);
                        }}
                    />
                </div>
                <div
                    className={`w-full flex flex-col justify-center gap-y-1 `}>
                    <Text className='text-base'>Resumen de la solicitud </Text>
                    <ResumenSolicitud data={indicadores || {}} />
                </div>
                <div className="w-full min-h-[300px] flex flex-col gap-y-2 my-2">
                    <Table
                        data={autorizaciones}
                        columns={columnasAutorizacionDinamicas}
                        selectable={false}
                        selected={selectedRow}
                    />
                    {showDropdown && <DroopdownGestion setOpenModal={setOpenGestionModal} setActionModal={setActionModal} />}
                </div>
            </div>
            {success && <FeedbackModal
                open={success}
                variant={success === "success" ? "success" : "error"}
                title={success === "success" ? feedbackMessages[actionModal].success?.title : feedbackMessages[actionModal].error?.title}
                message={success === "success" ? feedbackMessages[actionModal].success?.message : feedbackMessages[actionModal].error?.message}
                labelAccept={"Ir a Autorizaciones"}
                onAccept={() => navigate("/autorizaciones")}
                onIconClose={() => navigate("/autorizaciones")}
            />}

            <ModalRechazar
                open={openGestionModal && actionModal === "rechazar"}
                handleValue={rechazarSolicitud}
                setOpen={setOpenGestionModal}
                textAreaConfig={
                    { label: "Comentarios", placeholder: "Agregá por qué rechazás esta solicitud", isRequired: true }
                }
                text={`Asegurate de haber revisado toda la información enviada por el solicitante. Si la rechazás, la solicitud se marcará como rechazada y vas a poder indicar el motivo.`}
            />

            <MessagesModal
                openMessagesModal={openGestionModal && actionModal === "aceptar"}
                setOpenMessagesModal={setOpenGestionModal}
                onAccept={() => {
                    actionModal === "aceptar" && gestionarSolicitud()
                }}
                variant={"warning"}
                title={`${actionModal === "aceptar" ? "Aprobar" : "Rechazar"} solicitud`}
                message={`Si la ${actionModal === "aceptar" ? "aprobás" : "rechazás"}, se confirmará la solicitud y se iniciará el proceso correspondiente, notificando a las partes involucradas.`}
                labelAccept={`${actionModal === "aceptar" ? "Aprobar" : "Rechazar"} solicitud`}
                labelCancel={"Cancelar"}
            />
            <ModalComprobante closeModal={closeModal} isModalOpen={isModalOpen} adjunto={adjunto} />
        </>
    )
}

function getBreadCrumbs() {
    return [
        { label: "Inicio", href: "/", disabled: false },
        { label: "Autorizaciones", href: "/autorizaciones", disabled: false },
        { label: "Detalle de Solicitud", disabled: true },
    ];
}

ListaRendiciones.propTypes = {
    adjuntos: PropTypes.array,
    isModalOpen: PropTypes.bool,
    closeModal: PropTypes.func,
    adjunto: PropTypes.object,
}
